import React, {useState} from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import {useForm} from "react-hook-form";
import {Box, Button} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ProductRequest from "../../models/request/ProductRequest";
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {createMuiTheme, makeStyles, MuiThemeProvider} from '@material-ui/core/styles';
import Tooltip from "@material-ui/core/Tooltip";
import {green} from '@material-ui/core/colors';
import InputAdornment from '@material-ui/core/InputAdornment';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import {lengthBetween, onlyNumber} from "../Validations/HelperValidations";
import {Establishment} from "../../models/Establishment";
import BrandRequest from "../../models/request/BrandRequest";
import EstablishmentRequest from "../../models/request/EstablishmentRequest";

interface EstablishmentType {
    cuit: string,
    establishments: Establishment[],
    stepEstablishmentComplete: boolean,
    ecoNumberEntered: number,
    setEstablishments: any,
    handleNext(productRequest: ProductRequest, cuit: string): void
}

const colorBlue = "#023F86"

const useStyles = makeStyles({
    fontText: {
        color: "#000000",
        textAlign: 'left',
        marginLeft: 20,
        marginTop: 0,
        fontSize: 16,
     },
     HelpOutlineIconColor: {
         color: "#30c2c2"
     },
     fieldTextMargin: {
        marginTop: 20,
     },
     buttonContinuarStyle: {
        backgroundColor: colorBlue,
        color: '#FFFFFF',
        width: 230,
        height: 70
    },
    buttonAction: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingTop: '2rem',
    },
    tooltipStyle: {
        fontSize: 30,
        verticalAlign: "middle",
        marginLeft: 10,
    },
    tickStyle: {
        color: green.A400,
        fontSize: 40
    },
    tickTextStyle: {
        marginLeft: 15,
        marginRight: 15
    }
  });

const theme = createMuiTheme({
  overrides: {
    MuiTooltip: {
      tooltip: {
        color: colorBlue,
        backgroundColor: "white",
        border: '1px solid #ffffff',
        borderRadius: 5,
        boxShadow: '0px 5px 9px 1px rgba(0,0,0,0.83)',
        fontSize: 15,
        fontWeight: 400,
        maxWidth: 570
      }
    }
  }
});

const EstablishmentForm : React.FC<EstablishmentType> = ({cuit, establishments, stepEstablishmentComplete, ecoNumberEntered, handleNext, setEstablishments}) => {

    const [ecomNumber , setEcomNumber] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [okCheck, setOkCheck] = useState(stepEstablishmentComplete);
    

    const { handleSubmit } = useForm();

    const onSubmit = () => {
        setLoading(true);
        if(!existsEstablishmentCode(ecomNumber)) {
          setErrorMsg('El número de establecimiento que ingresaste no corresponde a tu comercio');
        } else {
          const productRequest = buildProductRequest(establishments)
          handleNext(productRequest, cuit)
          setOkCheck(true);
        }
        setLoading(false)
    }

    const existsEstablishmentCode = (code: string): boolean => {
      return establishments.some(e => e.code === code)
    }

    const buildProductRequest = (establishments: Establishment[]): ProductRequest => {
      return {
        code: "D+",
        brands: buildBrandRequest(establishments)
      }
    }

    const buildBrandRequest = (establishments: Establishment[]): BrandRequest[] => {
      const brandMap: Record<string, EstablishmentRequest[]> = {};
      establishments.forEach(e => {
        e.brands?.forEach(b => {
          const establishmentRequest: EstablishmentRequest = {
            code: e.code || "",
            terminals: e.terminals?.map(t => ({
              code: t.code || ""
               })
            ) || [],
            mcc: e.mcc || ""
          }
          if (!brandMap[b.code || ""]) {
            brandMap[b.code || ""] = [];
          }
          brandMap[b.code || ""].push(establishmentRequest);
        })
      })
      return Object.entries(brandMap).map(([brandCode, establishments]) => ({
        code: brandCode,
        establishments: establishments
      }))
  }

    const onChangeEcomNumber = (event: any) => {
        const numericValue = onlyNumber(event, 10)
        setOkCheck(false);
        if( lengthBetween(numericValue.length, 1, 10)) {
            setIsValid(true);
        } else {
            setIsValid(false);
            setErrorMsg('');
        }
        setEcomNumber(numericValue);
    }

    const classes = useStyles();

    return (
        <React.Fragment>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Box component="span" m={1}>
                            <p className={classes.fontText}>Ingresá uno de tus números de establecimiento para asegurarnos de que sos vos. Lo encontrás en el Portal de Autogestión.
                            <MuiThemeProvider theme={theme}>
                            <Tooltip
                                className={classes.tooltipStyle}
                                title={<span>
                                        <p className={classes.tickTextStyle}><b>¿Dónde encontrar el número de tu establecimiento?</b></p>
                                        <p className={classes.tickTextStyle}>1. Encontralo en el e-mail que recibiste al darte de alta en <b>Payway</b>.</p>
                                        <p className={classes.tickTextStyle}>2. Si tenés cuenta en PHE: ingresá al sitio web <b className={classes.HelpOutlineIconColor}>http://cuenta.payway.com.ar/</b> y seguí las instrucciones.</p>
                                       </span>}
                                placement="right" arrow>

                                <HelpOutlineIcon className={classes.HelpOutlineIconColor}/>
                            </Tooltip></MuiThemeProvider>
                            <p><strong>CUIT:</strong> {cuit}</p>
                            </p>

                        </Box>
                        <TextField
                            id={'establishment'}
                            className={classes.fieldTextMargin}
                            name="ecomNumber"
                            variant="filled"
                            label="Número de establecimiento *"
                            value={ecoNumberEntered != 0 ? ecoNumberEntered : ecomNumber}
                            onChange={onChangeEcomNumber}
                            error={!!errorMsg}
                            helperText={errorMsg || "Ingrese un número de establecimiento válido"}
                            disabled={ecoNumberEntered != 0}
                            fullWidth
                            InputProps={{
                                endAdornment:
                                <InputAdornment position="end">
                                    {okCheck ? <CheckBoxIcon className={classes.tickStyle}/>: null }
                                </InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {ecoNumberEntered == 0 ? (
                            <div className={classes.buttonAction}>
                                <Button
                                    id={'establishment-submit-button'}
                                    className={classes.buttonContinuarStyle}
                                    variant="contained"
                                    type="submit"
                                    disabled={!isValid || loading}
                                >
                                    CONTINUAR
                                </Button>
                                {loading && <CircularProgress size={24}/>}
                            </div>
                        ) : (
                            <p>Tu número de establecimiento fue verificado con éxito </p>
                        )}
                    </Grid>
                </Grid>
            </form>
        </React.Fragment>
    );
}

export default EstablishmentForm;